import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SHARED_MODULES } from '../../shared-imports';
import { ServerErrorDialogService, ServerErrorDialogState } from '../../../services/server-error-dialog.service';
import { AppConstants } from '../../../commons/app-constants';

type ReportState = 'idle' | 'sending' | 'done' | 'failed';

@Component({
    selector: 'app-server-error',
    templateUrl: './server-error.component.html',
    styleUrls: ['./server-error.component.scss'],
    imports: [SHARED_MODULES]
})
export class ServerErrorComponent implements OnInit, OnDestroy {
  visible = false;
  errorId = '';
  statusCode = 500;
  stackTrace?: string;
  appCode?: number;
  appCodeLabel?: string;

  reportState: ReportState = 'idle';
  returnedErrorCode = '';

  private http: HttpClient;
  private sub?: Subscription;
  private readonly endpoint = `${AppConstants.API_URL}api/v1/errors/user-report`;
  // This same component is used two different ways: (1) mounted once, permanently, in
  // app.component.html — the global HTTP interceptor calls ServerErrorDialogService.show() to
  // pop it as an overlay on top of whatever page the user is already on; (2) loaded fresh by the
  // router whenever something navigates straight to the /error/server-error ROUTE (e.g.
  // AccountDetailsResolver's catchError). Only case (2) is "this specific instance IS the page,
  // there is nothing else on screen" — confirmed live 2026-09-06: reaching it that way showed a
  // completely blank white page, because `visible` only ever gets set true by (1)'s mechanism.
  private isRoutedDirectly = false;

  constructor(
    private dialogService: ServerErrorDialogService,
    private router: Router,
    backend: HttpBackend,
  ) {
    // HttpBackend bypasses interceptors — avoids recursive error dialog on report failure
    this.http = new HttpClient(backend);
  }

  ngOnInit(): void {
    // Checked once, at construction — true only for case (2) above. The permanent app.component
    // instance is constructed once at app bootstrap against whatever the very first URL is, which
    // is never this route in normal use.
    this.isRoutedDirectly = this.router.url.startsWith('/error/server-error');
    if (this.isRoutedDirectly) {
      this.visible = true;
      this.statusCode = 500;
      this.errorId = '';
    }

    this.sub = this.dialogService.state$.subscribe((state: ServerErrorDialogState) => {
      this.visible = state.visible;
      if (state.visible) {
        this.errorId      = state.errorId;
        this.statusCode   = state.statusCode;
        this.stackTrace   = state.stackTrace;
        this.appCode      = state.appCode;
        this.appCodeLabel = state.appCodeLabel;
        this.reportState  = 'idle';
        this.returnedErrorCode = '';
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  close(): void {
    this.visible = false;
    this.dialogService.hide();
    // Overlay usage (mounted in app.component.html) just closes on top of whatever page was
    // already there — nothing else to do. Routed usage has no page behind it to fall back to,
    // so closing the dialog would otherwise leave a blank page with nothing on screen.
    if (this.isRoutedDirectly) {
      this.router.navigate(['/']);
    }
  }

  sendReport(): void {
    if (this.reportState !== 'idle') return;
    this.reportState = 'sending';

    this.http.post<{ errorCode: string; message: string }>(
      this.endpoint,
      {
        errorId:      this.errorId,
        statusCode:   this.statusCode,
        message:      this.errorTitle() + (this.appCode ? ` [${this.appCode}]` : ''),
        url:          window.location.href,
        userAgent:    navigator.userAgent,
        timestamp:    new Date().toISOString(),
        stackTrace:   this.stackTrace ?? null,
      },
      { withCredentials: true },
    ).subscribe({
      next: (res) => {
        this.returnedErrorCode = res.errorCode;
        this.reportState = 'done';
      },
      error: () => {
        this.reportState = 'failed';
      },
    });
  }

  retry(): void {
    this.reportState = 'idle';
  }

  errorTitle(): string {
    switch (this.statusCode) {
      // Softened deliberately (2026-07-26): "Internal Server Error" reads as more alarming/
      // technical than the situation warrants for a generic unhandled exception — the backend is
      // up and responding, one action failed. Reserve harder outage language for the codes below
      // (502/503/504) where the backend genuinely isn't responding as expected — that distinction
      // matters, so this must never become the catch-all default case's wording either.
      case 500: return 'Something went wrong';
      case 501: return 'Not Implemented';
      case 502: return 'Bad Gateway';
      case 503: return 'Service Unavailable';
      case 504: return 'Gateway Timeout';
      case 505: return 'HTTP Version Not Supported';
      default:  return `Server Error ${this.statusCode}`;
    }
  }

  errorSubtitle(): string {
    switch (this.statusCode) {
      case 500:
        return 'Our servers hit an unexpected snag. This has been automatically reported and our team is already looking into it.';
      case 501:
        return 'The server does not support the action you requested. Please contact support if this continues.';
      case 502:
        return 'An upstream server returned an invalid response. This is usually temporary — please try again in a moment.';
      case 503:
        return 'The server is temporarily unavailable, possibly under maintenance or overloaded. Please try again shortly.';
      case 504:
        return 'The server did not receive a timely response from an upstream service. Please try again in a moment.';
      case 505:
        return 'The server does not support the HTTP protocol version used by your client. Please contact support.';
      default:
        return 'An unexpected server-side error occurred. Please try again or contact support.';
    }
  }
}
